import { httpClient } from './http-client';
import type { ApiResponse } from './api.types';
import type { Product, AdditionalUnitMeasure } from '@/modelos/productos/Producto';
import type { ProductInput } from '@/hooks/productos/useProductoStore';
import type { ImportacionMasiva } from '@/modelos/ImportacionMasiva';

export interface ProductoDto {
  id: string;
  empresaId: string;
  tipoProducto: string | null;
  codigo: string | null;
  codigoSunat: string | null;
  codigoSunatNombre: string | null;
  codigoBarras: string | null;
  codigoFabrica: string | null;
  nombre: string;
  alias: string | null;
  descripcion: string | null;
  unidadMedidaId: string | null;
  unidadMedidaSimbolo: string | null;
  unidadMedidaNombre: string | null;
  unidadMedidaDescripcion: string | null;
  unidadMedidaCodigoSunat: string | null;
  unidadesMedidaAdicionales: string | null;
  afectacionTributariaId: string | null;
  afectacionTributariaCodigo: string | null;
  afectacionTributariaCodigoTributo: string | null;
  afectacionTributariaImpuesto: string | null;
  afectacionTributariaNombre: string | null;
  categoriaId: string | null;
  categoriaNombre: string | null;
  cuentaContableId: string | null;
  cuentaContableCodigo: string | null;
  familiaId: string | null;
  familiaNombre: string | null;
  centroCostoId: string | null;
  centroCostoNombre: string | null;
  presupuestoId: string | null;
  presupuestoNombre: string | null;
  esIcbper: boolean;
  precio: number;
  precioCompra: number | null;
  porcentajeGanancia: number | null;
  descuentoProducto: number | null;
  marca: string | null;
  modelo: string | null;
  peso: number | null;
  tipoExistencia: string | null;
  disponibleEnTodos: boolean;
  isFavorite: boolean;
  esActivo: boolean;
  stockPorEstablecimiento: string | null;
  stockPorAlmacen: string | null;
  stockReservadoPorAlmacen: string | null;
  stockMinimoPorAlmacen: string | null;
  stockMaximoPorAlmacen: string | null;
  esDetraccion: boolean;
  detraccionCodigo: string | null;
  detraccionId: string | null;
  detraccionNombre: string | null;
  detraccionPorcentaje: number | null;
  establecimientoIds: string[];
  usuarioId: string | null;
  usuarioNombre: string | null;
  imagenUrl?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ProductoInputDto {
  empresaId: string;
  tipoProducto?: string | null;
  codigo?: string | null;
  codigoSunat?: string | null;
  codigoSunatNombre?: string | null;
  codigoBarras?: string | null;
  codigoFabrica?: string | null;
  nombre: string;
  alias?: string | null;
  descripcion?: string | null;
  unidadMedidaId?: string | null;
  unidadMedidaSimbolo?: string | null;
  unidadMedidaNombre?: string | null;
  unidadMedidaDescripcion?: string | null;
  unidadMedidaCodigoSunat?: string | null;
  unidadesMedidaAdicionales?: string | null;
  afectacionTributariaId?: string | null;
  afectacionTributariaCodigo?: string | null;
  afectacionTributariaCodigoTributo?: string | null;
  afectacionTributariaImpuesto?: string | null;
  afectacionTributariaNombre?: string | null;
  categoriaId?: string | null;
  categoriaNombre?: string | null;
  cuentaContableId?: string | null;
  cuentaContableCodigo?: string | null;
  familiaId?: string | null;
  familiaNombre?: string | null;
  centroCostoId?: string | null;
  centroCostoNombre?: string | null;
  presupuestoId?: string | null;
  presupuestoNombre?: string | null;
  esIcbper?: boolean;
  precio: number;
  precioCompra?: number | null;
  porcentajeGanancia?: number | null;
  descuentoProducto?: number | null;
  marca?: string | null;
  modelo?: string | null;
  peso?: number | null;
  tipoExistencia?: string | null;
  disponibleEnTodos: boolean;
  isFavorite?: boolean;
  esActivo: boolean;
  stockPorEstablecimiento?: string | null;
  stockPorAlmacen?: string | null;
  stockReservadoPorAlmacen?: string | null;
  stockMinimoPorAlmacen?: string | null;
  stockMaximoPorAlmacen?: string | null;
  esDetraccion?: boolean;
  detraccionCodigo?: string | null;
  detraccionId?: string | null;
  detraccionNombre?: string | null;
  detraccionPorcentaje?: number | null;
  establecimientoIds: string[];
}

export interface ProductoImportarRequestDto {
  empresaId: string;
  productos: ProductoInputDto[];
}

export interface ProductoBuscarDto {
  id: string;
  codigo?: string | null;
  nombre: string;
  precio: number;
  alias?: string | null;
  unidadMedidaId?: string | null;
  unidadMedidaSimbolo?: string | null;
  unidadMedidaNombre?: string | null;
  unidadMedidaCodigoSunat?: string | null;
  afectacionTributariaId?: string | null;
  afectacionTributariaCodigo?: string | null;
  afectacionTributariaCodigoTributo?: string | null;
  afectacionTributariaImpuesto?: string | null;
  afectacionTributariaNombre?: string | null;
  tipoExistencia?: string | null;
  disponibleEnTodos: boolean;
  categoriaId?: string | null;
  categoriaNombre?: string | null;
  codigoSunat?: string | null;
  codigoBarras?: string | null;
  precioCompra?: number | null;
  descuentoProducto?: number | null;
  peso?: number | null;
  marca?: string | null;
  modelo?: string | null;
  codigoFabrica?: string | null;
  isFavorite: boolean;
  descripcion?: string | null;
  esDetraccion?: boolean;
  detraccionCodigo?: string | null;
  detraccionPorcentaje?: number | null;
  stockPorAlmacen?: string | null;
  stockReservadoPorAlmacen?: string | null;
  imagenUrl?: string | null;
}

export interface ProductoFiltrosParams {
  page?: number;
  pageSize?: number;
  busqueda?: string;
  categoriaId?: string;
  categoriaNombre?: string;
  unidadMedidaCodigo?: string;
  marca?: string;
  modelo?: string;
  afectacionTributariaCodigo?: string;
  establecimientoId?: string;
  almacenId?: string;
  soloDisponible?: boolean;
  soloBienes?: boolean;
  isFavorite?: boolean;
  esActivo?: boolean;
  ordenarPor?: string;
  direccion?: string;
}

export function parseJsonSafe<T>(valor: string | null, fallback: T): T {
  if (!valor) return fallback;
  try { return JSON.parse(valor) as T; }
  catch { return fallback; }
}

function serializarJsonSafe(valor: unknown): string | null {
  if (valor === undefined || valor === null) return null;
  try { return JSON.stringify(valor); }
  catch { return null; }
}

export function dtoAProducto(dto: ProductoDto): Product {
  // Construir label de impuesto formateado
  const buildTaxLabel = (): string | undefined => {
    if (!dto.afectacionTributariaNombre && !dto.afectacionTributariaImpuesto) {
      return undefined;
    }
    const nombre = dto.afectacionTributariaNombre || 'Impuesto';
    const cleanName = nombre.replace(/^IGV\s*\d*%?\s*/i, 'IGV').trim();
    const displayName = cleanName.toLowerCase().startsWith('igv') ? 'IGV' : cleanName;
    const impuesto = parseFloat(dto.afectacionTributariaImpuesto || '0');
    return `${displayName} (${impuesto.toFixed(2)}%)`;
  };

  return {
    id: dto.id,
    codigo: dto.codigo ?? '',
    nombre: dto.nombre,
    tipoProducto: dto.tipoProducto ?? undefined,
    unidad: dto.unidadMedidaCodigoSunat ?? '',
    unidadMedidaId: dto.unidadMedidaId ?? undefined,
    unitSymbol: dto.unidadMedidaSimbolo ?? undefined,
    unitName: dto.unidadMedidaNombre ?? undefined,
    unidadMedidaDescripcion: dto.unidadMedidaDescripcion ?? undefined,
    precio: dto.precio,
    categoria: dto.categoriaNombre ?? dto.categoriaId ?? '',
    categoriaId: dto.categoriaId ?? undefined,
    cuentaContableId: dto.cuentaContableId ?? undefined,
    cuentaContableCodigo: dto.cuentaContableCodigo ?? undefined,
    familiaId: dto.familiaId ?? undefined,
    familiaNombre: dto.familiaNombre ?? undefined,
    centroCostoId: dto.centroCostoId ?? undefined,
    centroCostoNombre: dto.centroCostoNombre ?? undefined,
    presupuestoId: dto.presupuestoId ?? undefined,
    presupuestoNombre: dto.presupuestoNombre ?? undefined,
    esIcbper: dto.esIcbper ?? false,
    esActivo: dto.esActivo,
    imagen: dto.imagenUrl ?? undefined,
    impuesto: buildTaxLabel(),
    afectacionTributariaId: dto.afectacionTributariaId ?? undefined,
    afectacionTributariaCodigo: dto.afectacionTributariaCodigo ?? undefined,
    afectacionTributariaCodigoTributo: dto.afectacionTributariaCodigoTributo ?? undefined,
    afectacionTributariaImpuesto: dto.afectacionTributariaImpuesto ?? undefined,
    afectacionTributariaNombre: dto.afectacionTributariaNombre ?? undefined,
    descripcion: dto.descripcion ?? undefined,
    unidadesMedidaAdicionales: parseJsonSafe<AdditionalUnitMeasure[]>(dto.unidadesMedidaAdicionales, []),
    establecimientoIds: dto.establecimientoIds ?? [],
    disponibleEnTodos: dto.disponibleEnTodos,
    alias: dto.alias ?? undefined,
    precioCompra: dto.precioCompra ?? undefined,
    porcentajeGanancia: dto.porcentajeGanancia ?? undefined,
    codigoBarras: dto.codigoBarras ?? undefined,
    codigoFabrica: dto.codigoFabrica ?? undefined,
    codigoSunat: dto.codigoSunat ?? undefined,
    codigoSunatNombre: dto.codigoSunatNombre ?? undefined,
    descuentoProducto: dto.descuentoProducto ?? undefined,
    marca: dto.marca ?? undefined,
    modelo: dto.modelo ?? undefined,
    isFavorite: dto.isFavorite,
    peso: dto.peso ?? undefined,
    tipoExistencia: (dto.tipoExistencia as Product['tipoExistencia']) ?? undefined,
    esDetraccion: dto.esDetraccion ?? false,
    detraccionCodigo: dto.detraccionCodigo ?? undefined,
    detraccionId: dto.detraccionId ?? undefined,
    detraccionNombre: dto.detraccionNombre ?? undefined,
    detraccionPorcentaje: dto.detraccionPorcentaje ?? undefined,
    cantidad: undefined,
    stockPorEstablecimiento: parseJsonSafe<Record<string, number>>(dto.stockPorEstablecimiento, {}),
    stockPorAlmacen: parseJsonSafe<Record<string, number>>(dto.stockPorAlmacen, {}),
    stockReservadoPorAlmacen: parseJsonSafe<Record<string, number>>(dto.stockReservadoPorAlmacen, {}),
    stockMinimoPorAlmacen: parseJsonSafe<Record<string, number>>(dto.stockMinimoPorAlmacen, {}),
    stockMaximoPorAlmacen: parseJsonSafe<Record<string, number>>(dto.stockMaximoPorAlmacen, {}),
    fechaCreacion: new Date(dto.createdAt),
    fechaActualizacion: new Date(dto.updatedAt),
  };
}

export function productoInputADto(input: ProductInput, empresaId: string): ProductoInputDto {
  return {
    empresaId,
    tipoProducto: input.tipoProducto || null,
    codigo: input.codigo || null,
    codigoSunat: input.codigoSunat || null,
    codigoSunatNombre: input.codigoSunatNombre || null,
    codigoBarras: input.codigoBarras || null,
    codigoFabrica: input.codigoFabrica || null,
    nombre: input.nombre,
    alias: input.alias || null,
    descripcion: input.descripcion || null,
    unidadMedidaId: input.unidadMedidaId || null,
    unidadMedidaCodigoSunat: input.unidad || null,
    unidadMedidaSimbolo: input.unitSymbol || null,
    unidadMedidaNombre: input.unitName || null,
    unidadMedidaDescripcion: input.unidadMedidaDescripcion || null,
    unidadesMedidaAdicionales: serializarJsonSafe(input.unidadesMedidaAdicionales),
    afectacionTributariaId: input.afectacionTributariaId || null,
    afectacionTributariaCodigo: input.afectacionTributariaCodigo || null,
    afectacionTributariaCodigoTributo: input.afectacionTributariaCodigoTributo || null,
    afectacionTributariaImpuesto: input.afectacionTributariaImpuesto || null,
    afectacionTributariaNombre: input.afectacionTributariaNombre || null,
    categoriaNombre: input.categoria || null,
    cuentaContableId: input.cuentaContableId ?? null,
    cuentaContableCodigo: input.cuentaContableCodigo ?? null,
    familiaId: input.familiaId ?? null,
    familiaNombre: input.familiaNombre ?? null,
    centroCostoId: input.centroCostoId ?? null,
    centroCostoNombre: input.centroCostoNombre ?? null,
    presupuestoId: input.presupuestoId ?? null,
    presupuestoNombre: input.presupuestoNombre ?? null,
    esIcbper: input.esIcbper ?? false,
    precio: 0,
    precioCompra: input.precioCompra ?? null,
    porcentajeGanancia: input.porcentajeGanancia ?? null,
    descuentoProducto: input.descuentoProducto ?? null,
    marca: input.marca || null,
    modelo: input.modelo || null,
    peso: input.peso ?? null,
    tipoExistencia: input.tipoExistencia || null,
    disponibleEnTodos: input.disponibleEnTodos,
    isFavorite: input.isFavorite ?? false,
    esActivo: true,
    esDetraccion: input.esDetraccion ?? false,
    detraccionCodigo: input.detraccionCodigo ?? null,
    detraccionId: input.detraccionId ?? null,
    detraccionNombre: input.detraccionNombre ?? null,
    detraccionPorcentaje: input.detraccionPorcentaje ?? null,
    stockPorEstablecimiento: serializarJsonSafe(input.stockPorEstablecimiento),
    stockPorAlmacen: serializarJsonSafe(input.stockPorAlmacen),
    stockReservadoPorAlmacen: serializarJsonSafe(input.stockReservadoPorAlmacen),
    stockMinimoPorAlmacen: serializarJsonSafe(input.stockMinimoPorAlmacen),
    stockMaximoPorAlmacen: serializarJsonSafe(input.stockMaximoPorAlmacen),
    establecimientoIds: input.establecimientoIds ?? [],
  };
}

export function productoADto(producto: Product): ProductoInputDto {
  return {
    empresaId: '',
    tipoProducto: producto.tipoProducto || null,
    codigo: producto.codigo || null,
    codigoSunat: producto.codigoSunat || null,
    codigoSunatNombre: producto.codigoSunatNombre || null,
    codigoBarras: producto.codigoBarras || null,
    codigoFabrica: producto.codigoFabrica || null,
    nombre: producto.nombre,
    alias: producto.alias || null,
    descripcion: producto.descripcion || null,
    unidadMedidaId: producto.unidadMedidaId || null,
    unidadMedidaCodigoSunat: producto.unidad || null,
    unidadMedidaSimbolo: producto.unitSymbol || null,
    unidadMedidaNombre: producto.unitName || null,
    unidadMedidaDescripcion: producto.unidadMedidaDescripcion || null,
    unidadesMedidaAdicionales: serializarJsonSafe(producto.unidadesMedidaAdicionales),
    afectacionTributariaId: producto.afectacionTributariaId || null,
    afectacionTributariaCodigo: producto.afectacionTributariaCodigo || null,
    afectacionTributariaCodigoTributo: producto.afectacionTributariaCodigoTributo || null,
    afectacionTributariaImpuesto: producto.afectacionTributariaImpuesto || null,
    afectacionTributariaNombre: producto.afectacionTributariaNombre || null,
    categoriaNombre: producto.categoria || null,
    cuentaContableId: producto.cuentaContableId ?? null,
    cuentaContableCodigo: producto.cuentaContableCodigo ?? null,
    familiaId: producto.familiaId ?? null,
    familiaNombre: producto.familiaNombre ?? null,
    centroCostoId: producto.centroCostoId ?? null,
    centroCostoNombre: producto.centroCostoNombre ?? null,
    presupuestoId: producto.presupuestoId ?? null,
    presupuestoNombre: producto.presupuestoNombre ?? null,
    esIcbper: producto.esIcbper ?? false,
    precio: producto.precio,
    precioCompra: producto.precioCompra ?? null,
    porcentajeGanancia: producto.porcentajeGanancia ?? null,
    descuentoProducto: producto.descuentoProducto ?? null,
    marca: producto.marca || null,
    modelo: producto.modelo || null,
    peso: producto.peso ?? null,
    tipoExistencia: producto.tipoExistencia || null,
    disponibleEnTodos: producto.disponibleEnTodos,
    isFavorite: producto.isFavorite ?? false,
    esActivo: producto.esActivo ?? true,
    esDetraccion: producto.esDetraccion ?? false,
    detraccionCodigo: producto.detraccionCodigo ?? null,
    detraccionId: producto.detraccionId ?? null,
    detraccionNombre: producto.detraccionNombre ?? null,
    detraccionPorcentaje: producto.detraccionPorcentaje ?? null,
    stockPorEstablecimiento: serializarJsonSafe(producto.stockPorEstablecimiento),
    stockPorAlmacen: serializarJsonSafe(producto.stockPorAlmacen),
    stockReservadoPorAlmacen: serializarJsonSafe(producto.stockReservadoPorAlmacen),
    stockMinimoPorAlmacen: serializarJsonSafe(producto.stockMinimoPorAlmacen),
    stockMaximoPorAlmacen: serializarJsonSafe(producto.stockMaximoPorAlmacen),
    establecimientoIds: producto.establecimientoIds ?? [],
  };
}

export interface ExportarProductosParams {
  busqueda?: string;
  categoriaId?: string;
  marca?: string;
  establecimientoId?: string;
  columnas?: string[];
}

function buildExportarProductosParams(params?: ExportarProductosParams): Record<string, string | undefined> {
  return {
    busqueda: params?.busqueda || undefined,
    categoriaId: params?.categoriaId || undefined,
    marca: params?.marca || undefined,
    establecimientoId: params?.establecimientoId || undefined,
    columnas: params?.columnas?.length ? params.columnas.join(',') : undefined,
  };
}

async function descargarBlobProductos(endpoint: string, params: Record<string, string | undefined>, filename: string): Promise<void> {
  const queryParams: Record<string, string | undefined> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value) queryParams[key] = value;
  }
  const blob = await httpClient.obtenerBlob(endpoint, queryParams);
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export const productosService = {
  listar: (params?: ProductoFiltrosParams): Promise<ApiResponse<ProductoDto[]>> =>
    httpClient.get('/productos', params as Record<string, string | number | boolean | undefined>),

  obtenerPorId: (id: string): Promise<ApiResponse<ProductoDto>> =>
    httpClient.get(`/productos/${id}`),

  crear: (data: ProductoInputDto): Promise<ApiResponse<ProductoDto>> =>
    httpClient.post('/productos', data),

  actualizar: (id: string, data: ProductoInputDto): Promise<ApiResponse<ProductoDto>> =>
    httpClient.put(`/productos/${id}`, data),

  eliminar: (id: string): Promise<ApiResponse<void>> =>
    httpClient.delete(`/productos/${id}`),

  validarEliminacion: (id: string): Promise<ApiResponse<{ tieneStock: boolean; tieneHistorial: boolean }>> =>
    httpClient.get(`/productos/${id}/validar-eliminacion`),

  generarCodigosBarras: (): Promise<ApiResponse<{ productosActualizados: number }>> =>
    httpClient.post('/productos/generar-codigos-barras'),

  importar: (data: ProductoImportarRequestDto): Promise<ApiResponse<ProductoDto[]>> =>
    httpClient.post('/productos/importar', data),

  bulkImport: (data: { modo: 'basica' | 'completa'; registros: ProductoInputDto[] }): Promise<ApiResponse<ImportacionMasiva>> =>
    httpClient.post('/productos/importar-masivo', data),

  descargarPlantillaImportacion: (tipo: 'basica' | 'completa'): Promise<Blob> =>
    httpClient.obtenerBlob('/productos/plantilla-importacion', { tipo }),

  exportar: (params?: Omit<ProductoFiltrosParams, 'page' | 'pageSize'>): Promise<ApiResponse<ProductoDto[]>> =>
    httpClient.get('/productos/exportar', params),

  exportarExcel: async (params?: ExportarProductosParams): Promise<void> => {
    const fecha = new Date().toISOString().split('T')[0];
    await descargarBlobProductos('/productos/exportar/excel', buildExportarProductosParams(params), `productos_${fecha}.xlsx`);
  },

  exportarCatalogoReporte: (params?: { establecimientoId?: string; busqueda?: string }): Promise<Blob> =>
    httpClient.obtenerBlob('/reportes/productos/excel', params),

  exportarCsv: async (params?: ExportarProductosParams): Promise<void> => {
    const fecha = new Date().toISOString().split('T')[0];
    await descargarBlobProductos('/productos/exportar/csv', buildExportarProductosParams(params), `productos_${fecha}.csv`);
  },

  exportarPdf: async (params?: ExportarProductosParams): Promise<void> => {
    const fecha = new Date().toISOString().split('T')[0];
    await descargarBlobProductos('/productos/exportar/pdf', buildExportarProductosParams(params), `productos_${fecha}.pdf`);
  },

  exportarPdfEtiquetas: async (params?: Omit<ExportarProductosParams, 'columnas'>): Promise<void> => {
    const fecha = new Date().toISOString().split('T')[0];
    await descargarBlobProductos('/productos/exportar/pdf-etiquetas', buildExportarProductosParams(params), `etiquetas_productos_${fecha}.pdf`);
  },

  exportarPdfEtiquetaProducto: async (productoId: string, cantidad: number, codigoProducto?: string): Promise<void> => {
    const fecha = new Date().toISOString().split('T')[0];
    const nombreBase = codigoProducto ? `etiquetas_${codigoProducto}` : 'etiquetas_producto';
    await descargarBlobProductos(
      '/productos/exportar/pdf-etiquetas',
      { productoId, cantidad: String(cantidad) },
      `${nombreBase}_${fecha}.pdf`,
    );
  },

  buscar: (params: {
    busqueda?: string;
    establecimientoId?: string;
    limit?: number;
    soloBienes?: boolean;
  }): Promise<ApiResponse<ProductoBuscarDto[]>> =>
    httpClient.get('/productos/buscar', params as Record<string, string | number | boolean | undefined>),
};
